import { AfterViewInit, Component, computed, ElementRef, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { MediaDevicePermissionWatcherService } from 'src/app/media-device-permission-watcher.service';
import { playBeep } from 'src/app/shared/helpers/audio.helper';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { DeviceSelectorComponent } from '../../../device-selector.component';
import { RecordingIndicatorComponent } from '../../audio-recorder/recording-indicator/recording-indicator.component';

declare let MediaRecorder: any;
type RecorderState = 'previewing' | 'countdown' | 'recording' | 'reviewing';

@Component({
  selector: 'app-video-recorder-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, MatProgressBarModule, TranslateModule, DeviceSelectorComponent, RecordingIndicatorComponent],
  template: `
    <div class="standard-dialog">
      <div class="header" mat-dialog-title>
        <div class="title-section">
          <h2>{{ (recorderState() === 'reviewing' ? 'VideoRecorder.Review.Title' : 'VideoRecorder.Title') | translate }}</h2>
          <p class="subtitle">
            {{ (recorderState() === 'reviewing' ? 'VideoRecorder.Review.Subtitle' : 'VideoRecorder.Subtitle') | translate }}
          </p>
        </div>
        <button mat-icon-button (click)="closeDialog()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content class="form-content">
        @if (recorderState() !== 'reviewing') {
          <div class="device-selectors-container">
            <app-device-selector
              [kind]="'videoinput'"
              [label]="'DeviceSelector.CameraLabel'"
              (selectionChange)="onCameraSelected($event)"
              [isDisabled]="isRecording()"
            ></app-device-selector>
            <app-device-selector
              [kind]="'audioinput'"
              [label]="'DeviceSelector.MicrophoneLabel'"
              (selectionChange)="onMicrophoneSelected($event)"
              [isDisabled]="isRecording()"
            ></app-device-selector>
          </div>
        }

        @if (isLoading()) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }

        <div
          class="video-container rounded-lg overflow-hidden bg-black flex items-center justify-center"
          [class.is-speaking]="isSpeaking()"
          [class.vertical-camera]="isVerticalCamera()"
        >
          <video
            #livePreview
            class="video-element"
            [class.hidden]="recorderState() === 'reviewing'"
            autoplay
            playsinline
            muted
            (loadedmetadata)="onMetadataLoaded(livePreview)"
          ></video>

          <video
            #recordedPlayback
            class="video-element"
            [class.hidden]="recorderState() !== 'reviewing'"
            [src]="recordedBlobUrl()"
            playsinline
            controls
            (loadedmetadata)="onMetadataLoaded(recordedPlayback)"
          ></video>

          @if (recorderState() === 'countdown' && countdown() !== null) {
            <div class="countdown-overlay">
              <span class="countdown-number">{{ countdown() }}</span>
            </div>
          }

          @if (isRecording() && recorderState() !== 'countdown') {
            <div class="recording-indicator">
              <span class="dot"></span>
              {{ 'VideoRecorder.Recording' | translate }}
            </div>
          }
        </div>
      </mat-dialog-content>

      <mat-dialog-actions class="footer mt-0! pt-0!">
        @if (recorderState() === 'previewing') {
          <div class="mx-auto">
            <button
              mat-flat-button
              class="!bg-[#dce1ff] !text-[#3e4759] hover:!bg-[#c5cbff] !rounded-full !h-11 !px-6 flex items-center gap-2 shadow-sm"
              (click)="startRecording()"
              [disabled]="isLoading()"
            >
              <mat-icon>camera_video</mat-icon>
              <span>{{ 'VideoRecorder.Record' | translate }}</span>
            </button>
          </div>
        }
        @if (isRecording()) {
          <div class="mx-auto">
            <app-recording-indicator [stream]="liveStream" [icon]="'stop'" [variant]="'danger'" (stopClick)="stopRecording()" [matTooltip]="'VideoRecorder.Stop' | translate">
            </app-recording-indicator>
          </div>
        }
        @if (recorderState() === 'reviewing') {
          <div class="right-actions ml-auto flex items-center gap-2">
            <button matButton (click)="restartRecording()">
              {{ 'VideoRecorder.RecordAgain' | translate }}
            </button>
            <button mat-button btnType="action" (click)="saveRecording()">
              {{ 'VideoRecorder.Save' | translate }}
            </button>
          </div>
        }
      </mat-dialog-actions>
    </div>
  `,
  styleUrls: ['../../recorders.scss'],
})
export class VideoRecorderDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('livePreview') livePreviewElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('recordedPlayback') recordedPlaybackElement!: ElementRef<HTMLVideoElement>;

  // --- State Signals ---
  recorderState = signal<RecorderState>('previewing');
  isLoading = signal(true);
  isRecording = computed(() => ['recording', 'countdown'].includes(this.recorderState()));
  recordedBlobUrl = signal<SafeUrl | null>(null);
  countdown = signal<number | null>(null);
  isVerticalCamera = signal(false);

  // --- New signals to hold the selected device IDs ---
  selectedCameraId = signal<string | null>(null);
  selectedMicrophoneId = signal<string | null>(null);

  // Audio analysis variables
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private dataArray: Uint8Array | null = null;
  private animationFrameId: number | null = null;

  volumeLevel = signal(0);
  isSpeaking = computed(() => this.volumeLevel() > 0.05 && this.recorderState() === 'previewing');

  private mediaRecorder: any;
  liveStream: MediaStream | null = null;
  private recordedBlobs: Blob[] = [];
  private finalBlob: Blob | null = null;
  private previewRequestId = 0;
  private countdownIntervalId: number | null = null;

  private dialogRef = inject(MatDialogRef<VideoRecorderDialogComponent>);
  private sanitizer = inject(DomSanitizer);
  private confirmService = inject(AppConfirmService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private permissionWatcher = inject(MediaDevicePermissionWatcherService);

  async ngAfterViewInit(): Promise<void> {
    // --- NEW LOGIC ---
    // Proactively load devices from localStorage to start the preview immediately.
    const savedCameraId = localStorage.getItem('selectedCamera');
    const savedMicrophoneId = localStorage.getItem('selectedMicrophone');

    // Set the signals with the saved values. The DeviceSelectorComponent will
    // read these same values and its initial state will match.
    this.selectedCameraId.set(savedCameraId);
    this.selectedMicrophoneId.set(savedMicrophoneId);

    // If we have both required IDs from storage, start the preview immediately.
    if (savedCameraId && savedMicrophoneId) {
      this.startCameraPreview();
    }
    // If not, we wait for the DeviceSelectorComponent to emit its initial selection.
  }

  ngOnDestroy(): void {
    this.stopAllStreams();
  }

  /**
   * Requests camera access using the currently selected device IDs.
   */
  async startCameraPreview(): Promise<void> {
    const requestId = ++this.previewRequestId;
    const cameraAccess = this.permissionWatcher.cameraPermissionState;
    const micAccess = this.permissionWatcher.microphonePermissionState;

    if (cameraAccess === 'denied' || micAccess === 'denied') {
      this.toast.showDevicePermissionError();
      return;
    }

    // Guard against running without selected devices.
    if (!this.selectedCameraId() || !this.selectedMicrophoneId()) {
      return;
    }

    this.isLoading.set(true);
    try {
      // Stop any existing stream before starting a new one.
      this.stopAllStreams();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: this.selectedCameraId()! }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: { deviceId: { exact: this.selectedMicrophoneId()! } },
      });

      if (requestId !== this.previewRequestId) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      this.liveStream = stream;
      this.livePreviewElement.nativeElement.srcObject = this.liveStream;
      this.livePreviewElement.nativeElement.muted = true;
      this.isLoading.set(false);
      this.startAudioAnalysis(this.liveStream);
    } catch (err: any) {
      console.error('Error accessing media devices:', err);
      this.isLoading.set(false);
      if (err.name === 'NotAllowedError' || err.message?.includes('Permission denied')) {
        this.toast.showDevicePermissionError();
      } else {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'An error occurred', 'OK', { duration: 3000 });
      }
    }
  }

  onMetadataLoaded(video: HTMLVideoElement): void {
    if (video && video.videoWidth && video.videoHeight) {
      const isVertical = video.videoHeight > video.videoWidth;
      this.isVerticalCamera.set(isVertical);
    }
  }

  // --- Handler methods for device selection ---
  onCameraSelected(deviceId: string): void {
    this.selectedCameraId.set(deviceId);
    // Restart the preview with the new camera.
    this.startCameraPreview();
  }

  onMicrophoneSelected(deviceId: string): void {
    this.selectedMicrophoneId.set(deviceId);
    if (this.recorderState() === 'previewing') {
      this.startCameraPreview();
    }
  }

  startRecording(): void {
    if (!this.liveStream || this.recorderState() !== 'previewing') return;

    this.recorderState.set('countdown');
    this.countdown.set(3);
    playBeep(880); // Call the imported helper function

    this.countdownIntervalId = window.setInterval(() => {
      this.countdown.update((val) => (val ?? 1) - 1);

      if (this.countdown() > 0) {
        playBeep(880); // Call the helper function
      } else if (this.countdown() === 0) {
        this.clearCountdown();
        playBeep(1200, true); // Call the helper function
        this.beginActualRecording();
      }
    }, 1000);
  }

  private beginActualRecording(): void {
    if (!this.liveStream) return;
    this.recorderState.set('recording');
    this.recordedBlobs = [];
    try {
      this.mediaRecorder = new MediaRecorder(this.liveStream, { mimeType: 'video/webm' });
      this.mediaRecorder.ondataavailable = (event: any) => {
        if (event.data && event.data.size > 0) this.recordedBlobs.push(event.data);
      };
      this.mediaRecorder.onstop = () => {
        this.finalBlob = new Blob(this.recordedBlobs, { type: 'video/webm' });
        const url = URL.createObjectURL(this.finalBlob);
        this.recordedBlobUrl.set(this.sanitizer.bypassSecurityTrustUrl(url));
        this.recorderState.set('reviewing');
        this.livePreviewElement.nativeElement.muted = true;
      };
      this.mediaRecorder.start();
    } catch (err) {
      console.error('Error creating MediaRecorder:', err);
      this.recorderState.set('previewing');
    }
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.isRecording()) {
      this.mediaRecorder.stop();
    }
  }

  async restartRecording(): Promise<void> {
    const confirmation = await firstValueFrom(
      this.confirmService.confirm({
        message: this.translate.instant('VideoRecorder.ConfirmRestart'),
      })
    );

    if (confirmation === 1) {
      this.finalBlob = null;
      this.recordedBlobUrl.set(null);
      this.recorderState.set('previewing');
      this.isVerticalCamera.set(false);
      if (this.livePreviewElement?.nativeElement) {
        this.livePreviewElement.nativeElement.muted = true;
      }
    }
  }

  saveRecording(): void {
    this.stopAllStreams();
    this.dialogRef.close(this.finalBlob);
  }

  async closeDialog(): Promise<void> {
    if (this.recorderState() === 'reviewing' && this.finalBlob) {
      const confirmation = await firstValueFrom(
        this.confirmService.confirm({
          message: this.translate.instant('VideoRecorder.ConfirmClose'),
        })
      );
      if (confirmation === 1) {
        this.stopAllStreams();
        this.dialogRef.close();
      }
    } else {
      this.stopAllStreams();
      this.dialogRef.close();
    }
  }

  private clearCountdown(): void {
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
    this.countdown.set(null);
  }

  private stopAllStreams(): void {
    this.clearCountdown();
    this.stopAudioAnalysis();
    this.isVerticalCamera.set(false);
    if (this.livePreviewElement?.nativeElement?.srcObject) {
      const stream = this.livePreviewElement.nativeElement.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      this.livePreviewElement.nativeElement.srcObject = null;
    }
    if (this.liveStream) {
      this.liveStream.getTracks().forEach((track) => track.stop());
      this.liveStream = null;
    }
    if (this.mediaRecorder && this.isRecording()) {
      try {
        this.mediaRecorder.stop();
      } catch (e) {}
    }
  }

  private startAudioAnalysis(stream: MediaStream): void {
    this.stopAudioAnalysis();
    try {
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);

      this.source = this.audioContext.createMediaStreamSource(stream);
      this.source.connect(this.analyser);

      this.analyzeAudio();
    } catch (err) {
      console.error('Failed to start audio analysis for video recorder preview', err);
    }
  }

  private analyzeAudio = () => {
    if (!this.analyser || !this.dataArray) return;
    this.animationFrameId = requestAnimationFrame(this.analyzeAudio);

    this.analyser.getByteFrequencyData(this.dataArray as Uint8Array<ArrayBuffer>);

    let sum = 0;
    for (const amplitude of this.dataArray) {
      sum += amplitude * amplitude;
    }
    const average = Math.sqrt(sum / this.dataArray.length);
    const normalizedVolume = Math.min(1, average / 128);

    this.volumeLevel.set(normalizedVolume);
  };

  private stopAudioAnalysis(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(console.error);
    }
    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;
    this.volumeLevel.set(0);
  }
}
